"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type MouseEvent,
  type ReactNode,
} from "react";

import { WaitlistModal } from "@/components/forms/WaitlistModal";
import { useLandingConfig } from "@/components/landing/LandingConfigProvider";
import {
  DEFAULT_STORE_AVAILABILITY,
  type StoreAvailability,
} from "@/lib/landing-config";
import {
  readVisitorCountryFromWindow,
  storeCtaDecision,
  storePlatformFromHref,
  type StorePlatform,
} from "@/lib/store-urls";

type StoreWaitlistContextValue = {
  visitorCountry: string | null;
  availability: StoreAvailability;
  /** False until the client has read the visitor country and landing config. */
  gateReady: boolean;
  shouldIntercept: (platform: StorePlatform) => boolean;
  openStoreWaitlist: (source: string, platform: StorePlatform) => void;
  interceptStoreClick: (
    event: { preventDefault: () => void },
    source: string,
    platform: StorePlatform,
  ) => boolean;
};

const StoreWaitlistContext = createContext<StoreWaitlistContextValue | null>(
  null,
);

function subscribeCountry(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("popstate", listener);
  return () => window.removeEventListener("popstate", listener);
}

export function StoreWaitlistProvider({ children }: { children: ReactNode }) {
  const { config, loading } = useLandingConfig();
  const availability = config.storeAvailability ?? DEFAULT_STORE_AVAILABILITY;
  const visitorCountry = useSyncExternalStore(
    subscribeCountry,
    readVisitorCountryFromWindow,
    () => null,
  );
  const [clientReady, setClientReady] = useState(false);

  useEffect(() => {
    setClientReady(true);
  }, []);

  // SSR country is null, which used to mean "intercept". Wait until the
  // client has actually read the country and the live store lists.
  const gateReady = clientReady && !loading;

  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("store_waitlist");
  const [platform, setPlatform] = useState<StorePlatform>("ios");

  const resolvedCountry = () => readVisitorCountryFromWindow() ?? visitorCountry;

  const shouldIntercept = useCallback(
    (nextPlatform: StorePlatform) =>
      storeCtaDecision(
        nextPlatform,
        resolvedCountry(),
        availability,
        gateReady,
      ) === "waitlist",
    // resolvedCountry reads window at call time; visitorCountry retriggers after hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availability, gateReady, visitorCountry],
  );

  useEffect(() => {
    if (!open || !gateReady) return;
    if (
      storeCtaDecision(platform, resolvedCountry(), availability, true) ===
      "store"
    ) {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, gateReady, open, platform, visitorCountry]);

  const openStoreWaitlist = useCallback(
    (nextSource: string, nextPlatform: StorePlatform) => {
      setSource(nextSource);
      setPlatform(nextPlatform);
      setOpen(true);
    },
    [],
  );

  const interceptStoreClick = useCallback(
    (
      event: { preventDefault: () => void },
      nextSource: string,
      nextPlatform: StorePlatform,
    ) => {
      if (!shouldIntercept(nextPlatform)) return false;
      event.preventDefault();
      openStoreWaitlist(nextSource, nextPlatform);
      return true;
    },
    [openStoreWaitlist, shouldIntercept],
  );

  const value = useMemo(
    () => ({
      visitorCountry,
      availability,
      gateReady,
      shouldIntercept,
      openStoreWaitlist,
      interceptStoreClick,
    }),
    [
      availability,
      gateReady,
      interceptStoreClick,
      openStoreWaitlist,
      shouldIntercept,
      visitorCountry,
    ],
  );

  return (
    <StoreWaitlistContext.Provider value={value}>
      {children}
      <WaitlistModal
        open={open}
        onClose={() => setOpen(false)}
        source={source}
        platform={platform}
        country={visitorCountry}
      />
    </StoreWaitlistContext.Provider>
  );
}

export function useStoreWaitlist(): StoreWaitlistContextValue {
  const ctx = useContext(StoreWaitlistContext);
  if (!ctx) {
    throw new Error("useStoreWaitlist must be used within StoreWaitlistProvider");
  }
  return ctx;
}

/**
 * Intercept App Store / Play Store anchors when the visitor country is not
 * in that platform's published list. Keep hrefs in the DOM for SEO.
 */
export function handleStoreAnchorClick(
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
  source: string,
  interceptStoreClick: StoreWaitlistContextValue["interceptStoreClick"],
) {
  const platform = storePlatformFromHref(href);
  if (!platform) return;
  interceptStoreClick(event, source, platform);
}
